import type { ReactElement } from "react";
import { Callout, Text } from "../ui-kit";
import type { RequiredConfig } from "../config";
import styles from "./ConfigurationRequired.module.css";

/** Runtime config key → the environment variable an operator actually sets. */
const ENV_VAR_BY_KEY: Record<keyof RequiredConfig, string> = {
  entraClientId: "WEB_ADMIN_ENTRA_CLIENT_ID",
  entraAuthority: "WEB_ADMIN_ENTRA_AUTHORITY",
  entraApiScope: "WEB_ADMIN_ENTRA_API_SCOPE",
  apiBaseUrl: "WEB_ADMIN_API_BASE_URL",
};

export interface ConfigurationRequiredProps {
  missing: readonly (keyof RequiredConfig)[];
}

/**
 * The configuration-error screen.
 *
 * Shown when `config.js` did not arrive with every required runtime value. The
 * app deliberately stops here rather than starting a sign-in against a partial
 * configuration, and it names the missing environment variables so an operator
 * can fix the deploy without reading the bundle.
 *
 * It lists NAMES only — never a value, and never anything read from a token.
 */
export function ConfigurationRequired({ missing }: ConfigurationRequiredProps): ReactElement {
  return (
    <Callout tone="warning" title="This deployment is not fully configured">
      <Text variant="body" className={styles.body}>
        Sign-in has not been attempted, because starting one with an incomplete configuration
        would fail in a way that looks like a rejected account. Set the following in the
        environment&apos;s variable group and redeploy:
      </Text>
      <ul className={styles.list}>
        {missing.map((key) => (
          <li key={key}>
            <Text as="span" variant="identifier">
              {ENV_VAR_BY_KEY[key]}
            </Text>
          </li>
        ))}
      </ul>
    </Callout>
  );
}
